import { IGeoConstraint } from '@faire/web-api--source/faire/geo/IGeoConstraint';
import { IMeasurements } from '@faire/web-api--source/faire/measurements/IMeasurements';
import { IFaireMoney } from '@faire/web-api--source/indigofair/data/IFaireMoney';
import { IImage } from '@faire/web-api--source/indigofair/data/IImage';
import { IItemPromoPrice } from '@faire/web-api--source/indigofair/data/IItemPromoPrice';
import { IProduct } from '@faire/web-api--source/indigofair/data/IProduct';
import { IProductVariation } from '@faire/web-api--source/indigofair/data/IProductVariation';
import { ISizingQuantity } from '@faire/web-api--source/indigofair/data/ISizingQuantity';
import { IWholesalePriceByQuantity } from '@faire/web-api--source/indigofair/data/IWholesalePriceByQuantity';

export namespace IProductOption {
  export namespace IPrice {
    /**
     * Return an instance of IPrice with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (partial?: Partial<IPrice>): IPrice =>
      ({
        ...(partial ?? {}),
      }) as IPrice;
  }

  /**
   * The tags are out of order. All tag numbers must be unique. When adding fields, also update the field below and the tag number.
   * See https://www.notion.so/faire/Protocol-Buffers-Protos-ba527d2f70c34ab983b8d0417c6a56ac#6a792ceb9d37498795a117e712b9e7e7 for details.
   * The last tag added was `gtin`. The next tag number is 59.
   * Holds alternative pricing.
   */
  export interface IPrice {
    geo_constraint: IGeoConstraint | undefined;
    wholesale_price: IFaireMoney | undefined;
    retail_price: IFaireMoney | undefined;
    /**
     * If specified, this is the reduced price for this option given a running item-level Promotion
     */
    wholesale_promo_price: IItemPromoPrice | undefined;
  }

  /**
   * POs are used to represent purchasable options to the client, and as such they don't map 1:1 to
   * real DbProductOptions on the database. In fact, we have a few types of "fake" product options,
   * whose tokens come from some place else.
   */
  export enum Type {
    /**
     * The real ones, the token is a DbProductOption token (`po_`)
     */
    REGULAR = 'REGULAR',
    /**
     * This was a product option, but has been converted to Mix&Match, and now has a `mmpo_` token.
     */
    MIX_AND_MATCH = 'MIX_AND_MATCH',
    /**
     * A prepack might generate fake options to be purchased.
     * In this case, the token is from the underlying DbProductCollection (`pc_`)
     */
    PRE_PACK = 'PRE_PACK',
    /**
     * This PO represents a DISPLAY, which is derived from DbProduct.Type.DISPLAY *
     */
    DISPLAY = 'DISPLAY',
  }

  export enum OrderabilityType {
    ORDERABILITY_TYPE_UNKNOWN = 'ORDERABILITY_TYPE_UNKNOWN',
    IMMEDIATE = 'IMMEDIATE',
    PREORDER = 'PREORDER',
  }

  export enum ProductOptionLevelPrepackState {
    PRODUCT_OPTION_LEVEL_PREPACK_STATE_UNKNOWN = 'PRODUCT_OPTION_LEVEL_PREPACK_STATE_UNKNOWN',
    /**
     * This product option is a product option level prepack
     */
    ACTIVE = 'ACTIVE',
    /**
     * This product option is not a product option level prepack now, it used to be one
     */
    INACTIVE = 'INACTIVE',
  }

  /**
   * Return an instance of IProductOption with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (partial?: Partial<IProductOption>): IProductOption =>
    ({
      prices: [],
      images: [],
      variations: [],
      sizing_quantity: [],
      wholesale_price_by_quantity_list: [],
      ...(partial ?? {}),
    }) as IProductOption;
}

/**
 * @protobufOption (enableProductModelFiltering) = true
 */
export interface IProductOption {
  /**
   * @protobufOption (.faire.common.tokenType) = PRODUCT_OPTION
   * @protobufOption (.faire.wire_cache.identifier) = true
   */
  token: string | undefined;
  /**
   * TODO (Raf)
   * WILL BE REMOVED
   * Whether this product option is active.
   * Deprecated in favor of checking [lifecycleState] and [state]
   * @deprecated
   */
  active: boolean | undefined;
  /**
   * TODO (Raf)
   * WILL BE REMOVED
   * The option is marked as deleted, it won't be returned in any API anymore.
   * Deprecated in favor of checking [lifecycleState] and [state]
   * @deprecated
   */
  deleted: boolean | undefined;
  /**
   * DEPRECATED - A ProductOption token
   * @deprecated
   */
  parent_token: string | undefined;
  /**
   * The name of this product option. It is a concatenation of the ordered ProductVariation Values.
   * Ex: Variations = [Size: M, Colour:Red] -> Name = M / Red
   * Deprecated in favor of [displayName] which is localized
   * @deprecated
   */
  name: string | undefined;
  /**
   * The SKU number assigned to this product option by the brand.
   */
  brand_code: string | undefined;
  /**
   * The Global Trade Item Number (GTIN) assigned to this product option by the brand.
   */
  gtin: string | undefined;
  description: string | undefined;
  /**
   * Deprecated in favour of [prices] and [wholesalePrice]
   * @deprecated
   */
  wholesale_price_cents: number | undefined;
  /**
   * READ-ONLY
   * A single price.
   * In USD during backend processing (i.e. after DbProductOption.toWire).
   * In the requester's currency when sent to clients (i.e. after CountrySpecificPricingFilter runs).
   */
  wholesale_price: IFaireMoney | undefined;
  /**
   * READ-ONLY
   * a single price served in the requesters currency if a promotional price exists
   */
  wholesale_promo_price: IItemPromoPrice | undefined;
  /**
   * Deprecated in favour of [prices] and [retailPrice]
   * @deprecated
   */
  retail_price_cents: number | undefined;
  /**
   * READ-ONLY
   * A single price.
   * In USD during backend processing (i.e. after DbProductOption.toWire).
   * In the requester's currency when sent to clients (i.e. after CountrySpecificPricingFilter runs).
   */
  retail_price: IFaireMoney | undefined;
  /**
   * An unordered list of country-specific prices.
   * On Retailer READ, should be redacted down to only the prices we want to provide to a given customer.
   * @protobufOption (.faire.common.diffAs) = UNORDERED
   * @protobufOption (.faire.common.uniqueKey) = "geoConstraint"
   */
  prices: Array<IProductOption.IPrice>;
  /**
   * Also known as case size or case quantity, this is the unit in which this option ships.
   */
  unit_multiplier: number | undefined;
  /**
   * An ordered list of images for display.
   * @protobufOption (.faire.common.diffAs) = ORDERED
   * @protobufOption (.faire.common.uniqueKey) = "token"
   */
  images: Array<IImage>;
  /**
   * When this option should become available for sale again.
   */
  backordered_until_date: number | undefined;
  /**
   * Whether this product option offers testers.
   */
  has_testers: boolean | undefined;
  /**
   * @deprecated This value is deprecated, please read this value from other protos or access
   *             ProductOptionAvailableInventoryFetcher directly.
   * If non-null, how much stock this option has. Normally, retailers can only purchase this much.
   *    Based on other factors, such as [Product.allowSalesWhenOutOfStock] retailers may be able to purchase more
   *    than this amount.
   * If null, assume infinite stock.
   * @deprecated
   */
  available_units: number | undefined;
  /**
   * The measurements of this specific option.
   */
  measurements: IMeasurements | undefined;
  /**
   * The case measurements of this specific option.
   * @protobufOption (exposedProductModelContext) = BRAND_VIEW_CONTEXT
   */
  case_measurements: IMeasurements | undefined;
  /**
   * @protobufOption (.faire.common.tokenType) = PRODUCT
   */
  product_token: string | undefined;
  idempotence_token: string | undefined;
  /**
   * An ordered list of variations ordered by the entry name ordinal in the variation map.
   * @protobufOption (.faire.common.diffAs) = ORDERED
   */
  variations: Array<IProductVariation>;
  tester_price_cents: number | undefined;
  tester_price: IFaireMoney | undefined;
  tester_promo_price: IItemPromoPrice | undefined;
  /**
   * @protobufOption (.faire.wire_cache.isLocalized) = true
   */
  display_name: string | undefined;
  tariff_code: string | undefined;
  predicted_tariff_code: string | undefined;
  /**
   * True if this option is sold in any prepacks.
   */
  sold_in_pre_packs: boolean | undefined;
  /**
   * To be used only internal by the server, not for external APIs.
   * @protobufOption (.faire.common.diffIgnore) = ALWAYS
   * @protobufOption (.indigofair.data.redacted) = true
   */
  id: number | undefined;
  /**
   * For now, MOQ is equal to the parent product's, unless it's a prepack
   */
  min_order_quantity: number | undefined;
  /**
   * The type of token backing this PO, see Type for more details
   */
  type: keyof typeof IProductOption.Type | undefined;
  /**
   * The unit count per cart item. For Pre Packs, one cart item represents several units.
   */
  unit_count_per_cart_item: number | undefined;
  /**
   * eventual replacement field for active & deleted
   */
  state: keyof typeof IProduct.State | undefined;
  /**
   * the product lifecycle state
   */
  lifecycle_state: keyof typeof IProduct.LifecycleState | undefined;
  /**
   * Shown to brands to represent inventory state
   */
  brand_availability: keyof typeof IProduct.BrandAvailability | undefined;
  sizing_quantity: Array<ISizingQuantity>;
  brand_token: string | undefined;
  /**
   * Shipping window for this pre-order product.
   * This is the start (first day) of the window in milliseconds.
   * The window lasts for expectedShipWindowDays days.
   */
  expected_ship_date: number | undefined;
  /**
   * Shipping window for this pre-order product.
   * This is how many days after [expectedShipDate] the window is.
   * The default is 0, meaning there is no window, and products/orders ship exactly on expectedShipDate.
   */
  expected_ship_window_days: number | undefined;
  orderability_type: keyof typeof IProductOption.OrderabilityType | undefined;
  /**
   * [Product.keepActivePastOrderByDate] == true -> [stopSellingAt] = null
   * [Product.orderByDate] != null -> [stopSellingAt] = [Product.orderByDate]
   * [Product.expectedShipDate] != null -> [stopSellingAt] = [Product.expectedShipDate]
   */
  stop_selling_at: number | undefined;
  /**
   * @protobufOption (.faire.common.diffIgnore) = ALWAYS
   */
  version: number | undefined;
  /**
   * @protobufOption (.indigofair.data.redacted) = true
   */
  created_at: number | undefined;
  /**
   * @protobufOption (.faire.common.diffIgnore) = ALWAYS
   * @protobufOption (.indigofair.data.redacted) = true
   */
  updated_at: number | undefined;
  product_option_level_prepack_state:
    | keyof typeof IProductOption.ProductOptionLevelPrepackState
    | undefined;
  units_per_product_option_level_prepack: number | undefined;
  wholesale_price_by_quantity_list: Array<IWholesalePriceByQuantity>;
}
